import { Icon, SelectOutlined } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { PeriodOption } from '@/hooks/objectives/usePeriodOptions';

import { ALL_PERIODS_OPTION_ID, CUSTOM_DATES_OPTION_ID } from '@/constants/ObjectivePeriodLens';

interface PeriodSelectProps {
    options: PeriodOption[];
    /** The selected entry: a period id, or `CUSTOM_DATES_OPTION_ID`. */
    value: string;
    onChange: (periodId: string) => void;
    /** Appends the "custom dates" entry, under the cycles and outside their scroll. */
    allowCustomDates?: boolean;
    /**
     * Prepends the "all periods" lens. A list filter only: an objective is saved on one cycle or on
     * custom dates, never on every cycle at once.
     */
    allowAllPeriods?: boolean;
    /** Whether the period list is still on its way; an empty list then means nothing yet. */
    isLoading?: boolean;
    /**
     * `toolbar` (default) is the glass control of the pages' filter bars; `field` is the form's
     * full-width tinted field, the same surface as the employee picker stacked above it.
     */
    surface?: 'toolbar' | 'field';
}

const SURFACES = {
    toolbar: {
        wrapper: 'obj-period-control',
        trigger: 'backdrop-blur-sm',
        background: 'bg-white/70 hover:bg-white/90',
        border: 'border border-white',
    },
    field: {
        wrapper: 'w-full',
        trigger: 'min-h-[46px]',
        background: 'bg-surface-field',
        border: 'border border-transparent',
    },
} as const;

/** Both ends of the trigger sit as far from its text as before the icon moved into it. */
const TRIGGER_INSET = { paddingLeft: 20, paddingRight: 20 };

/**
 * The cycle select of the module's toolbars: the same glass surface and radius as the search and
 * the attribute filter beside it (`TOOLBAR_CONTROL_SURFACE`), with a leading calendar glyph.
 *
 * With nothing to offer -- no period created yet, or none active on the form -- the trigger says
 * so and stops being operable rather than opening an empty list; the pages behind it stay usable.
 * That verdict waits for the list to arrive: while the request is in flight the list is empty too,
 * and announcing "no objective period" then would state as fact something nobody has checked.
 */
export default function PeriodSelect({
    options,
    value,
    onChange,
    allowCustomDates = false,
    allowAllPeriods = true,
    isLoading = false,
    surface = 'toolbar',
}: PeriodSelectProps) {
    const { t } = useTranslation();
    const periodEntries = options.map((option) => ({
        id: option.id,
        value: option.id,
        label: option.label,
    }));
    // Pinned under the cycles, outside their scroll, and set apart by its glyph and a line saying
    // what it does, so nobody takes it for one more cycle.
    const customDatesEntries = allowCustomDates
        ? [
              {
                  id: CUSTOM_DATES_OPTION_ID,
                  value: CUSTOM_DATES_OPTION_ID,
                  label: t('objectives.period.customDates'),
                  description: t('objectives.period.customDatesDescription'),
                  startIcon: (
                      <Icon
                          name={'time-calendar-edit'}
                          size={'sm'}
                          color={'inherit'}
                          ariaHidden
                      />
                  ),
              },
          ]
        : [];
    // Above the cycles, because it is the only lens that can show an objective whose dates fall
    // outside every one of them. Offered with no cycle at all too: it is then what the lists read.
    const allPeriodsEntries = allowAllPeriods
        ? [
              {
                  id: ALL_PERIODS_OPTION_ID,
                  value: ALL_PERIODS_OPTION_ID,
                  label: t('objectives.period.allPeriods'),
              },
          ]
        : [];
    const listEntries = [...allPeriodsEntries, ...periodEntries];
    const hasListEntries = listEntries.length > 0;
    const hasEntries = hasListEntries || allowCustomDates;
    const hasNothingToOffer = !isLoading && !hasEntries;

    return (
        <div className={SURFACES[surface].wrapper}>
            <SelectOutlined
                // With nothing to sit under, the custom dates are the list itself rather than a
                // footer below an empty one.
                options={hasListEntries ? listEntries : customDatesEntries}
                footerOptions={hasListEntries ? customDatesEntries : undefined}
                value={value}
                onChange={(nextValue) => onChange(String(nextValue))}
                placeholder={t(
                    hasNothingToOffer ? 'objectives.period.none' : 'objectives.form.selectPeriod',
                )}
                disabled={isLoading || !hasEntries}
                // Periods read in time order, most recent first, the way the API serves them --
                // the library's default alphabetical sort would shuffle them by name.
                sort={'none'}
                ariaLabel={t('objectives.period.label')}
                leadingIcon={
                    <Icon
                        name={'time-calendar'}
                        size={'sm'}
                        colorClassName={'text-obj-slate'}
                        ariaHidden
                    />
                }
                triggerStyle={TRIGGER_INSET}
                triggerClassName={SURFACES[surface].trigger}
                triggerBackgroundClassName={SURFACES[surface].background}
                triggerBorderClassName={SURFACES[surface].border}
                textClassName={'text-navy'}
                dataTestId={'objectives-period-select'}
            />
        </div>
    );
}
